// ignore-string-externalization

import { createLoader } from '@spotify-internal/creator-data-loading';
import { FaqArticle, FaqCategory } from './util';
import { fetchEntry, fetchEntryList } from '../Contentful';
import { HelpHomeTemplateType } from '../Template/Help/HelpHomeTemplate';
import logger from '@spotify-internal/isomorphic-logger';
import { getHelpHomePage } from './graphql/getHelpHomePage';

type HelpLoaderParams = {
  key?: string;
  smartlingLanguage: string;
  preview: boolean;
};

type HelpQuery<T> = (params: HelpLoaderParams) => Promise<T | null>;

const helpLoader = <T>(query: HelpQuery<T>) => {
  const loader = createLoader<HelpLoaderParams, T | undefined>(
    params =>
      query(params)
        .then(result => result || undefined)
        .catch(e => {
          logger.error(e);
          return undefined;
        }),
    {
      cacheKeyFn: JSON.stringify,
    },
  );

  return (params: HelpLoaderParams) => loader.load(params);
};

export const loadHome = helpLoader<HelpHomeTemplateType>(
  ({ preview, smartlingLanguage }) =>
    getHelpHomePage(preview, smartlingLanguage),
);

export const loadCategory = helpLoader<FaqCategory>(
  ({ key, preview, smartlingLanguage }) =>
    key
      ? fetchEntry<FaqCategory>({
          contentType: 'faqCategory',
          filters: { urlSlug: key },
          locale: smartlingLanguage,
          preview,
        })
      : Promise.reject('category urlSlug not provided'),
);

export const loadRelatedCategory = helpLoader<FaqCategory>(
  ({ key, preview, smartlingLanguage }) =>
    fetchEntryList<FaqCategory>({
      contentType: 'faqCategory',
      linksToEntry: key,
      locale: smartlingLanguage,
      preview,
    }).then(result => result?.entries?.[0]),
);

export const loadFaq = helpLoader<FaqArticle>(
  ({ key, preview, smartlingLanguage }) =>
    key
      ? fetchEntry<FaqArticle>({
          contentType: 'faq',
          filters: { urlSlug: key },
          locale: smartlingLanguage,
          preview,
        })
      : Promise.reject('faq urlSlug not provided'),
);
